import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { BoardPanel } from '../board/BoardPanel';
import { BrassButton, layoutButtonRow } from '../board/BrassButton';
import { TitlePlaque } from '../board/TitlePlaque';
import { animate, COLOURS, displayStyle, ease, uiStyle } from '../board/ui';
import { linkLasts } from '../../logic/account/linkLasts';

/**
 * What the account board is showing:
 * - `create`: the offer, an email and the marketing box;
 * - `signin`: from Settings: an email, to sign in on this device or create
 *   the account if the address has none (and the marketing box);
 * - `sent`: the link is on its way;
 * - `created` / `welcome`: a link was followed (a new account, or signed in);
 * - `expired`: a link that was used or ran out;
 * - `manage`: the account, and the marketing box;
 * - `error`: something went wrong, said in `message`.
 */
export type AccountMode = 'create' | 'signin' | 'sent' | 'created' | 'welcome' | 'expired' | 'manage' | 'error';

/** What the player pressed. */
export type AccountPress = 'send' | 'cancel' | 'ok' | 'new-link' | 'marketing';

export interface AccountView {
  mode: AccountMode;
  email?: string;
  /** The marketing box (create and manage). */
  marketing?: boolean;
  /** The email typed looks like an address (create and signin). */
  emailValid?: boolean;
  /** A link is being asked for. */
  busy?: boolean;
  /** For `error`. */
  message?: string;
  /** How long a link works. */
  linkMinutes?: number;
}

export interface AccountArt {
  floor: Texture | null;
  envelope: Texture | null;
  tickOff: Texture | null;
  tickOn: Texture | null;
}

/** Where the email field sits, in canvas pixels. */
export interface FieldRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The board as plain data, for tests. */
export interface AccountUi {
  mode: AccountMode;
  title: string;
  lines: string[];
  buttons: { label: string; enabled: boolean }[];
  marketing: boolean | null;
  field: boolean;
}

const MAX_BOARD_W = 380;
const PAD = { top: 32, side: 20, bottom: 20 };
const MARGIN = 12;
const FIELD_H = 50;
const BUTTON_H = 48;
const TICK = 34;

const TITLES: Record<AccountMode, string> = {
  create: 'CREATE YOUR ACCOUNT',
  signin: 'PLAY ON ANY DEVICE',
  sent: 'CHECK YOUR EMAIL',
  created: "YOU'RE ALL SET",
  welcome: 'WELCOME BACK',
  expired: 'LINK RAN OUT',
  manage: 'YOUR ACCOUNT',
  error: 'SORRY',
};

/**
 * The optional account, on the board kit: the offer to create one, signing
 * in on another device, "check your email", and the account itself. The
 * email field is the page's own input laid over the board's field (the
 * host places it at {@link fieldRect}), so phones bring up their keyboard
 * and fill in addresses as usual.
 */
export class AccountScreen extends Container {
  private readonly dim = new Graphics();
  private readonly card = new Container();
  private readonly board: BoardPanel;
  private readonly plaque = new TitlePlaque(TITLES.create, 17);
  private readonly content = new Container();
  private readonly envelope = new Sprite();
  private readonly lead = new Text({ text: '', style: { ...uiStyle(16, COLOURS.cream, '600'), align: 'center', wordWrap: true } });
  private readonly address = new Text({ text: '', style: { ...displayStyle(20), align: 'center', wordWrap: true, breakWords: true } });
  private readonly body = new Text({ text: '', style: { ...uiStyle(14, COLOURS.cream, '500'), align: 'center', wordWrap: true } });
  private readonly fieldLabel = new Text({ text: 'Your email', style: uiStyle(14, COLOURS.cream, '600') });
  private readonly field = new Graphics();
  private readonly tickRow = new Container();
  private readonly tickBox = new Sprite();
  private readonly tickDrawn = new Graphics();
  private readonly tickLabel = new Text({ text: 'Email me news and free gifts', style: { ...uiStyle(14, COLOURS.cream, '600'), wordWrap: true } });
  private readonly footnote = new Text({ text: '', style: { ...uiStyle(12, COLOURS.cream, '500'), align: 'center', wordWrap: true } });
  private readonly cancelButton: BrassButton;
  private readonly actionButton: BrassButton;

  private view: AccountView = { mode: 'create' };
  private viewW = 1;
  private viewH = 1;
  private visibleTop = 0;
  private visibleH = 1;
  private contentW = 300;
  private fieldBox: FieldRect | null = null;

  constructor(
    private readonly art: AccountArt,
    private readonly onPress: (press: AccountPress) => void,
    private readonly onField: (rect: FieldRect | null) => void,
  ) {
    super();
    this.board = new BoardPanel({ floor: art.floor });
    this.dim.eventMode = 'static';
    this.dim.on('pointertap', () => {
      if (!this.view.busy) this.onPress('cancel');
    });
    if (art.envelope) this.envelope.texture = art.envelope;
    this.envelope.anchor.set(0.5, 0);
    this.tickBox.anchor.set(0, 0.5);
    this.tickLabel.anchor.set(0, 0.5);
    this.tickRow.addChild(this.tickBox, this.tickDrawn, this.tickLabel);
    this.tickRow.eventMode = 'static';
    this.tickRow.cursor = 'pointer';
    this.tickRow.on('pointertap', () => this.onPress('marketing'));
    this.cancelButton = new BrassButton({ label: 'Not now', kind: 'wood', size: 17, onTap: () => this.onPress('cancel') });
    this.actionButton = new BrassButton({ label: 'SEND LINK', kind: 'gold', size: 19, onTap: () => this.pressAction() });
    this.content.addChild(
      this.envelope,
      this.lead,
      this.address,
      this.body,
      this.fieldLabel,
      this.field,
      this.tickRow,
      this.cancelButton,
      this.actionButton,
      this.footnote,
    );
    this.card.addChild(this.board, this.content, this.plaque);
    this.addChild(this.dim, this.card);
  }

  /** Show a mode (or the same one with new details). */
  show(view: AccountView): void {
    this.view = view;
    this.refresh();
  }

  get mode(): AccountMode {
    return this.view.mode;
  }

  /** The field's place on the canvas, or null when the mode has none. */
  get fieldRect(): FieldRect | null {
    return this.fieldBox;
  }

  /**
   * Lay out over a `viewW` by `viewH` canvas. `visible` is the part of it
   * not under a phone's keyboard; the board stays inside it.
   */
  layout(viewW: number, viewH: number, visible?: { top: number; height: number }): void {
    this.viewW = viewW;
    this.viewH = viewH;
    this.visibleTop = visible?.top ?? 0;
    this.visibleH = visible?.height ?? viewH;
    this.dim.clear().rect(0, 0, viewW, viewH).fill({ color: 0x0a0602, alpha: 0.72 });
    const boardW = Math.min(MAX_BOARD_W, Math.max(260, viewW - 2 * MARGIN));
    this.contentW = boardW - 2 * PAD.side;
    const contentH = this.layoutContent();
    const boardH = contentH + PAD.top + PAD.bottom;
    this.board.resize(boardW, boardH);
    this.content.position.set(PAD.side, PAD.top);
    this.plaque.position.set(boardW / 2, 0);
    const rise = this.plaque.plateHeight / 2;
    const room = this.visibleH - 2 * MARGIN;
    const scale = Math.min(1, room / (boardH + rise));
    this.card.scale.set(scale);
    this.card.pivot.set(boardW / 2, 0);
    const cardH = (boardH + rise) * scale;
    const top = this.visibleTop + Math.max(MARGIN, (this.visibleH - cardH) / 2);
    this.card.position.set(Math.round(viewW / 2), Math.round(top + rise * scale));
    this.reportField(boardW, scale);
  }

  async open(): Promise<void> {
    this.visible = true;
    this.alpha = 0;
    await animate(200, (t) => {
      this.alpha = ease.outCubic(t);
    });
  }

  async close(): Promise<void> {
    await animate(150, (t) => {
      this.alpha = 1 - t;
    });
    this.visible = false;
  }

  ui(): AccountUi {
    const buttons = [this.cancelButton, this.actionButton]
      .filter((b) => b.visible)
      .map((b) => ({ label: b.labelText, enabled: b.alpha === 1 }));
    const lines = [this.lead, this.address, this.body, this.footnote].filter((t) => t.visible && t.text).map((t) => t.text);
    return {
      mode: this.view.mode,
      title: TITLES[this.view.mode],
      lines,
      buttons,
      marketing: this.tickRow.visible ? this.view.marketing === true : null,
      field: this.fieldBox !== null,
    };
  }

  /** Where a button (by its label) or the marketing box sits, for tests. */
  where(label: string): { x: number; y: number } | null {
    const target =
      label === 'marketing'
        ? this.tickRow.visible
          ? this.tickBox
          : null
        : [this.cancelButton, this.actionButton].find((b) => b.visible && b.labelText === label) ?? null;
    if (!target) return null;
    const b = target.getBounds();
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }

  private pressAction(): void {
    const { mode } = this.view;
    if (mode === 'create' || mode === 'signin') this.onPress('send');
    else if (mode === 'expired') this.onPress('new-link');
    else this.onPress('ok');
  }

  private refresh(): void {
    const v = this.view;
    const lasts = linkLasts(v.linkMinutes ?? 24 * 60);
    this.plaque.setTitle(TITLES[v.mode]);
    const asks = v.mode === 'create' || v.mode === 'signin';
    const ticks = asks || v.mode === 'manage';
    this.envelope.visible = (v.mode === 'sent' || v.mode === 'created' || v.mode === 'welcome') && this.art.envelope !== null;
    this.fieldLabel.visible = asks;
    this.field.visible = asks;
    this.tickRow.visible = ticks;
    this.address.visible = false;
    this.footnote.visible = false;
    this.body.visible = false;
    let lead = '';
    switch (v.mode) {
      case 'create':
        lead = 'Add your email to keep your coins and progress safe, and to play on any device.';
        this.footnote.text = "We'll email you a link to confirm. No password needed.";
        this.footnote.visible = true;
        break;
      case 'signin':
        lead = "Enter your email and we'll send you a link. It signs you in here, or creates your account if you don't have one.";
        this.footnote.text = 'No password needed.';
        this.footnote.visible = true;
        break;
      case 'sent':
        lead = "We've sent a link to";
        this.address.text = v.email ?? '';
        this.address.visible = true;
        this.body.text = `Tap it to finish. It works once, for ${lasts}. Can't see it? Look in your spam folder.`;
        this.body.visible = true;
        break;
      case 'created':
        lead = 'Your account is ready.';
        this.body.text = 'Sign in with this email on any device and carry on where you left off.';
        this.body.visible = true;
        this.address.text = v.email ?? '';
        this.address.visible = true;
        break;
      case 'welcome':
        lead = "You're signed in as";
        this.address.text = v.email ?? '';
        this.address.visible = true;
        this.body.text = 'Your coins and progress are all here.';
        this.body.visible = true;
        break;
      case 'expired':
        lead = 'That link has been used or has run out. Send yourself a new one.';
        break;
      case 'manage':
        lead = "You're signed in as";
        this.address.text = v.email ?? '';
        this.address.visible = true;
        this.body.text = 'Sign in with this email on any device to play your game there.';
        this.body.visible = true;
        break;
      case 'error':
        lead = v.message ?? "We couldn't reach the game just now. Try again in a moment.";
        break;
    }
    this.lead.text = lead;
    this.drawTick(v.marketing === true);

    const twoButtons = v.mode === 'create' || v.mode === 'signin' || v.mode === 'expired';
    this.cancelButton.visible = twoButtons;
    this.cancelButton.setLabel(v.mode === 'create' ? 'Not now' : 'Cancel');
    this.cancelButton.setEnabled(!v.busy);
    const action =
      v.mode === 'create' || v.mode === 'signin'
        ? v.busy
          ? 'SENDING'
          : 'SEND LINK'
        : v.mode === 'expired'
          ? 'NEW LINK'
          : v.mode === 'created' || v.mode === 'welcome'
            ? 'PLAY'
            : v.mode === 'manage'
              ? 'DONE'
              : 'OK';
    this.actionButton.setLabel(action);
    this.actionButton.setEnabled(asks ? v.emailValid === true && !v.busy : true);
    if (this.viewW > 1) this.layout(this.viewW, this.viewH, { top: this.visibleTop, height: this.visibleH });
  }

  private drawTick(on: boolean): void {
    const tex = on ? this.art.tickOn : this.art.tickOff;
    this.tickDrawn.clear();
    if (tex) {
      this.tickBox.texture = tex;
      this.tickBox.visible = true;
      this.tickBox.scale.set(TICK / Math.max(tex.width, tex.height));
      return;
    }
    // Before the painted box loads.
    this.tickBox.visible = false;
    this.tickDrawn
      .roundRect(0, -TICK / 2, TICK, TICK, 8)
      .fill({ color: COLOURS.woodDark })
      .stroke({ color: COLOURS.brass, width: 3 });
    if (on) {
      this.tickDrawn
        .moveTo(8, 0)
        .lineTo(14, 7)
        .lineTo(26, -8)
        .stroke({ color: COLOURS.gold, width: 5, cap: 'round', join: 'round' });
    }
  }

  /** Place everything inside the board; returns the content's height. */
  private layoutContent(): number {
    const w = this.contentW;
    for (const t of [this.lead, this.address, this.body, this.footnote]) {
      t.style.wordWrapWidth = w;
      t.anchor.set(0.5, 0);
      t.x = w / 2;
    }
    let y = 0;
    if (this.envelope.visible && this.art.envelope) {
      const h = 84;
      this.envelope.scale.set(h / this.art.envelope.height);
      this.envelope.position.set(w / 2, y);
      y += h + 10;
    }
    this.lead.y = y;
    y += this.lead.height + 8;
    if (this.address.visible) {
      // A long address shrinks to fit rather than run off the board.
      this.address.style.fontSize = 20;
      while (this.address.width > w && Number(this.address.style.fontSize) > 13) {
        this.address.style.fontSize = Number(this.address.style.fontSize) - 1;
      }
      this.address.y = y;
      y += this.address.height + 8;
    }
    if (this.body.visible) {
      this.body.y = y;
      y += this.body.height + 8;
    }
    if (this.field.visible) {
      y += 4;
      this.fieldLabel.position.set(2, y);
      y += this.fieldLabel.height + 6;
      this.field
        .clear()
        .roundRect(0, y + 2, w, FIELD_H, 12)
        .fill({ color: COLOURS.brassLight, alpha: 0.18 })
        .roundRect(0, y, w, FIELD_H, 12)
        .fill({ color: 0x120a03, alpha: 0.82 })
        .stroke({ color: COLOURS.brass, width: 2 })
        .roundRect(3, y + 3, w - 6, 8, 5)
        .fill({ color: 0x000000, alpha: 0.35 });
      this.fieldLocal = { x: 0, y, width: w, height: FIELD_H };
      y += FIELD_H + 12;
    } else {
      this.fieldLocal = null;
    }
    if (this.tickRow.visible) {
      this.tickLabel.style.wordWrapWidth = w - TICK - 12;
      this.tickLabel.x = TICK + 12;
      const rowH = Math.max(TICK, this.tickLabel.height);
      this.tickRow.position.set(0, y + rowH / 2);
      this.tickRow.hitArea = { contains: (x: number, yy: number) => x >= -4 && x <= w && yy >= -rowH / 2 - 4 && yy <= rowH / 2 + 4 };
      y += rowH + 14;
    }
    y += 4;
    this.cancelButton.resize(this.cancelButton.buttonWidth, BUTTON_H);
    this.actionButton.resize(this.actionButton.buttonWidth, BUTTON_H);
    layoutButtonRow(this.cancelButton.visible ? this.cancelButton : null, this.actionButton, 0, y, w);
    y += BUTTON_H;
    if (this.footnote.visible) {
      this.footnote.y = y + 10;
      y = this.footnote.y + this.footnote.height;
    }
    return y;
  }

  private fieldLocal: FieldRect | null = null;

  private reportField(boardW: number, scale: number): void {
    if (!this.fieldLocal) {
      this.fieldBox = null;
      this.onField(null);
      return;
    }
    const left = this.card.x - (boardW / 2) * scale;
    const top = this.card.y;
    this.fieldBox = {
      x: left + (PAD.side + this.fieldLocal.x) * scale,
      y: top + (PAD.top + this.fieldLocal.y) * scale,
      width: this.fieldLocal.width * scale,
      height: this.fieldLocal.height * scale,
    };
    this.onField(this.fieldBox);
  }
}
